import _ from "lodash";
import React, {
  PureComponent,
  useState,
  useRef,
  cloneElement,
  useEffect,
} from "react";
import { compose } from "lib/apolloClient";
import classes from "./SinglePointRubricWithReordering.scss";
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import { colors, fontStyle } from "Constants";
import { connect } from "react-redux";
import classNames from "classnames";
import { updateField } from "modules/FieldUpdateThunks";
import {
  writeSinglePointRubricElementLocal,
  createSinglePointRubricStandard,
  deleteSinglePointRubricStandard,
} from "SinglePointRubric/modules/SinglePointRubricModule";
import { Button, Dialog, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  AssessmentApplicableOutlined,
  DotsSixVerticalOutlined,
  AddColumnOutlined,
} from "@toddle-edu/ds-icons";
import { updateEvaluationData } from "ClassRoom/modules/ClassRoomModule";
import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";

import {
  DndContext,
  closestCenter,
  useSensors,
  useSensor,
  useDraggable,
  MouseSensor,
  TouchSensor,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { elevations } from "@toddle-edu/ds-theme";
import { moveElementInArray, useIntersectionObserver } from "Utils";
import { SINGLE_POINT_RUBRIC } from "Constants/stringConstants";
import { reorderAssessmentToolItem } from "modules/AssessmentTool/AssessmentToolModule";
import { getItemBeforeAndAfter } from "Utils/Common";
import {
  getProjectionLineData,
  sortAssessmentTool,
} from "ClassRoom/modules/utils";
import { Popover } from "@toddle-edu/react-tiny-popover";

const textAreaStyles = {
  cellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: "0 24px 0 0",
    color: colors.textDefault,
    lineHeight: 1.57,
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.bold,
    overflow: "hidden",
  },
  headerCellTitle: {
    lineHeight: 1.5,
    fontSize: "1.6rem",
    padding: 0,
    color: colors.textDefault,
  },
  cellSubTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textSubtle,
    lineHeight: 1.57,
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.medium,
  },
  headerSubCellTitle: {
    display: "flex",
    color: colors.textSubtle,
  },
  cellResponse: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textDefault,
    lineHeight: 1.57,
    letterSpacing: "-0.1px",
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.medium,
  },
};

const splitViewTextAreaStyles = {
  cellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textDefault,
    lineHeight: "2rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.demiBold,
    overflow: "hidden",
  },
  evaluationCellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.black,
    lineHeight: "2rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.demiBold,
    overflow: "hidden",
  },
  cellSubTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textSubtle,
    lineHeight: "1.6rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.medium,
  },
  cellResponse: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textDefault,
    lineHeight: "2rem",
    letterSpacing: "-0.1px",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.medium,
  },
  inputStyle: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: fontStyle.medium,
    fontSize: "1.4rem",
    color: colors.textDisabled,
  },
};

const styles = {
  inputStyle: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: fontStyle.medium,
    fontSize: "1.6rem",
    color: colors.textDisabled,
  },
  containerStyle: {
    height: "100%",
  },
  popoverContainerStyle: {
    zIndex: 1100,
  },
  rowPopoverContainerStyle: {
    top: "-10px",
    left: "-10px",
  },
  rotateIconStyle: {
    transform: "rotate(90deg)",
  },
  scaleIconStyle: {
    transform: "scaleX(-1)",
  },
  transformIconStyle: {
    transform: "rotate(90deg) scaleX(-1)",
  },
  popoverIconStyle: {
    transform: "rotate(90deg)",
    width: "20px",
    height: "16px",
  },
};

const mergedStyles = {
  textAreaHeaderCellStyles: {
    ...textAreaStyles.cellTitle,
    ...textAreaStyles.headerCellTitle,
  },
  textAreaHeaderCellStylesSplitView: {
    ...textAreaStyles.cellTitle,
    ...textAreaStyles.headerCellTitle,
    ...splitViewTextAreaStyles.cellTitle,
  },
  textAreaHeaderCellSubStyles: {
    ...textAreaStyles.cellSubTitle,
    ...textAreaStyles.headerSubCellTitle,
  },
  textAreaHeaderCellSubStylesSplitView: {
    ...textAreaStyles.cellSubTitle,
    ...textAreaStyles.headerSubCellTitle,
    ...splitViewTextAreaStyles.cellSubTitle,
  },
  evaluationCellTitleStyles: {
    ...textAreaStyles.cellTitle,
    ...splitViewTextAreaStyles.evaluationCellTitle,
  },
  textAreaCellSubTitleStylesSplitView: {
    ...splitViewTextAreaStyles.cellSubTitle,
    color: colors.textDefault,
  },
  rowPopoverStyles: {
    ...styles.popoverContainerStyle,
    ...styles.rowPopoverContainerStyle,
  },
};

function DraggableRow({ id, children, projectedLineData, standards }) {
  const disableDragging = _.some(standards, standard =>
    _.includes(_.get(standard, "id", null), "NEW")
  );
  const props = useDraggable({ id, disabled: disableDragging });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    node,
  } = props;

  let style = {
    transform: !_.isEmpty(transform)
      ? `translate3d(${transform.x}px, ${transform.y}px, 0px)`
      : "",
    transition,
    zIndex: isDragging ? 100 : 1,
    cursor: isDragging ? "grabbing" : "",
    opacity: isDragging ? 0.75 : "",
  };

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id,
    disabled: disableDragging,
  });

  if (projectedLineData?.id === id) {
    style = { ...style, ...projectedLineData.style };
  }

  return (
    <div
      id={id}
      className={classes.pseudoRow}
      ref={ref => {
        setNodeRef(ref);
        setDroppableNodeRef(ref);
      }}
      style={style}
    >
      {cloneElement(children, {
        node,
        attributes,
        listeners,
        isDragging,
        disableDragging,
      })}
    </div>
  );
}

const Header = props => {
  const {
    t,
    criteriaObj,
    mode,
    isSplitView = false,
    inFocusCell,
    updateInputRef,
    setFocusedCell,
    writeSinglePointRubricElement,
    writeSinglePointRubricElementLocal,
    saveSinglePointRubricUpdateActivityStatus,
    setSelectedStandard,
  } = props;

  const [title, setTitle] = useState(criteriaObj?.title);
  const [subTitle, setSubTitle] = useState(criteriaObj?.subTitle);

  const refTitleKey = `Header_${criteriaObj.id}_Title`;
  const refSubtitleKey = `Header_${criteriaObj.id}_Subtitle`;

  const disabled = _.includes(criteriaObj.id, "NEW");

  const updateInputFieldTitle = params => {
    setTitle(params.title);
    writeSinglePointRubricElement({
      id: criteriaObj.id,
      params,
      type: "CRITERIA",
      elementDetails: criteriaObj,
      isTitle: true,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const updateInputFieldSubtitle = params => {
    setSubTitle(params.subTitle);
    writeSinglePointRubricElement({
      id: criteriaObj.id,
      params,
      type: "CRITERIA",
      elementDetails: criteriaObj,
      isTitle: false,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const cellClass = classNames({
    [classes.rowHeaderCell]: true,
    [classes.focusedCell]: inFocusCell == criteriaObj.id,
    [classes.disabled]: disabled,
  });

  return (
    <div className={classes.pseudoColumn} key={criteriaObj.id}>
      <div className={cellClass} onFocus={() => setFocusedCell(criteriaObj.id)}>
        <TextAreaInput
          value={title}
          mode={mode}
          textAreaStyles={
            isSplitView
              ? mergedStyles.textAreaHeaderCellStylesSplitView
              : mergedStyles.textAreaHeaderCellStyles
          }
          updateInputRef={ref => updateInputRef({ ref, refKey: refTitleKey })}
          onBlurInputField={() => {
            setFocusedCell(null);
            writeSinglePointRubricElementLocal({
              id: criteriaObj.id,
              params: { title },
              type: "CRITERIA",
              elementDetails: criteriaObj,
              isTitle: true,
            });
          }}
          onFocusInputField={() => {
            setFocusedCell(refTitleKey);
            setSelectedStandard({});
          }}
          disabled={disabled}
          minRows={1}
          emptyText={""}
          name={"title"}
          placeholder={t("common:click_to_add_withLabel", {
            label: t("common:standard"),
          })}
          updateInputField={updateInputFieldTitle}
          className={classes.placeholderTextOnSubtle}
        />
        <TextAreaInput
          value={subTitle}
          mode={mode}
          textAreaStyles={
            isSplitView
              ? mergedStyles.textAreaHeaderCellSubStylesSplitView
              : mergedStyles.textAreaHeaderCellSubStyles
          }
          updateInputRef={ref =>
            updateInputRef({ ref, refKey: refSubtitleKey })
          }
          onBlurInputField={() => {
            setFocusedCell(null);
            writeSinglePointRubricElementLocal({
              id: criteriaObj.id,
              params: { subTitle },
              type: "CRITERIA",
              elementDetails: criteriaObj,
              isTitle: false,
            });
          }}
          onFocusInputField={() => {
            setFocusedCell(refSubtitleKey);
            setSelectedStandard({});
          }}
          disabled={disabled}
          minRows={1}
          name={"subTitle"}
          emptyText={""}
          placeholder={t("common:definition")}
          updateInputField={updateInputFieldSubtitle}
          className={classes.placeholderColor}
        />
      </div>
    </div>
  );
};

const Columns = props => {
  const {
    t,
    mode,
    rubric,
    criteriaObj,
    standardObj,
    isSplitView = false,
    inFocusCell,
    cellStyle,
    setFocusedCell,
    updateInputRef,
    writeSinglePointRubricElement,
    deleteSinglePointRubricStandard,
    writeSinglePointRubricElementLocal,
    saveSinglePointRubricUpdateActivityStatus,
    hoverStandardDetails,
    setSelectedStandard,
    selectedStandard,
    createSinglePointRubricStandard,
    isDragging,
    listeners,
    attributes,
    node,
    rowIndex,
    setIndexToFocus,
    indexToFocus,
    disableDragging,
  } = props;

  const [title, setTitle] = useState(standardObj?.title);
  const [subTitle, setSubTitle] = useState(standardObj?.subTitle);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleDeleteModal = () => setShowDeleteModal(prev => !prev);
  const onConfirmDelete = () => {
    deleteSinglePointRubricStandard(standardObj?.id);
    setSelectedStandard(null);
    toggleDeleteModal();
    if (rowIndex > 0) setIndexToFocus(rowIndex - 1);
    else setIndexToFocus(rowIndex);
  };
  const rowActionsRef = useRef(null);
  const textAreaRef = useRef(null);
  const isFullyVisible = useIntersectionObserver(node, {
    threshold: 0.7,
  });
  // check if the selected item is in the view or not
  useEffect(() => {
    if (
      !isFullyVisible &&
      !isDragging &&
      selectedStandard === standardObj?.id
    ) {
      setSelectedStandard({});
    }
  }, [isFullyVisible]);

  const standards = _.get(rubric, "standards", []);
  const isCriteriaStandardColumn = criteriaObj.isCriteriaStandardColumn;
  const showDeleteIcon = _.size(standards) > 1;

  const refTitleKey = `Row_${standardObj.id}_Title`;
  const refSubtitleKey = `Row_${standardObj.id}_Subtitle`;
  const disabled = _.includes(standardObj.id, "NEW");

  const applyContainerStyle =
    mode === "view" &&
    _.isEmpty(standardObj?.title) &&
    _.isEmpty(standardObj?.subTitle);

  const inputStyle = applyContainerStyle
    ? isSplitView
      ? splitViewTextAreaStyles.inputStyle
      : styles.inputStyle
    : {};

  const updateInputFieldTitle = params => {
    setTitle(params.title);
    writeSinglePointRubricElement({
      id: standardObj.id,
      params,
      type: "STANDARD",
      elementDetails: standardObj,
      isTitle: true,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const updateInputFieldSubtitle = params => {
    setSubTitle(params.subTitle);
    writeSinglePointRubricElement({
      id: standardObj.id,
      params,
      type: "STANDARD",
      elementDetails: standardObj,
      isTitle: false,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const onAddStandardAfter = async () => {
    setSelectedStandard({});
    await createSinglePointRubricStandard({ putAfterItemId: standardObj.id });
    setIndexToFocus(rowIndex + 1);
  };
  const onAddStandardBefore = async () => {
    const { before } = getItemBeforeAndAfter(standards, standardObj.id);
    setSelectedStandard({});
    await createSinglePointRubricStandard({
      putAfterItemId: before?.id || null,
    });
    setIndexToFocus(rowIndex);
  };

  const cellClass = classNames({
    [classes.cell]: true,
    [classes.focusedCell]:
      inFocusCell == standardObj.id && isCriteriaStandardColumn,
    [classes.nonCriteriaCell]: !isCriteriaStandardColumn,
    [classes.splitViewNonCriteriaCell]:
      !isCriteriaStandardColumn && isSplitView,
    [classes.disabled]: disabled,
    [classes.draggingCell]: isDragging && isCriteriaStandardColumn,
  });

  const rowActionsClass = classNames({
    [classes.rowActions]: true,
    [classes.activeColumnActions]: selectedStandard === standardObj.id,
  });

  const enableActionPopover =
    node.current &&
    (hoverStandardDetails.id === standardObj.id ||
      standardObj.id === selectedStandard) &&
    !isDragging &&
    isFullyVisible;

  const enableActionPopoverOption =
    standardObj.id === selectedStandard &&
    rowActionsRef.current &&
    isFullyVisible;

  if (indexToFocus === rowIndex) {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }

  return (
    <div
      className={cellClass}
      key={criteriaObj.id}
      style={cellStyle}
      onFocus={() => setFocusedCell(standardObj.id)}
    >
      {isCriteriaStandardColumn ? (
        <React.Fragment>
          <TextAreaInput
            value={title}
            textAreaStyles={
              isSplitView
                ? mergedStyles.evaluationCellTitleStyles
                : textAreaStyles.cellTitle
            }
            updateInputRef={ref => updateInputRef({ ref, refKey: refTitleKey })}
            onBlurInputField={() => {
              if (!_.isNull(indexToFocus)) setIndexToFocus(null);
              setFocusedCell(null);
              writeSinglePointRubricElementLocal({
                id: standardObj.id,
                params: { title },
                type: "STANDARD",
                elementDetails: standardObj,
                isTitle: true,
              });
            }}
            ref={textAreaRef}
            onFocusInputField={() => {
              setFocusedCell(refTitleKey);
              setSelectedStandard({});
            }}
            minRows={1}
            emptyText={applyContainerStyle ? t("common:no_criteria_added") : ""}
            inputStyle={inputStyle}
            containerStyle={applyContainerStyle ? styles.containerStyle : {}}
            disabled={disabled}
            mode={mode}
            placeholder={t("unitPlan:enter_criteria_title")}
            name={"title"}
            updateInputField={updateInputFieldTitle}
            className={classes.placeholderColor}
          />
          <TextAreaInput
            value={subTitle}
            textAreaStyles={
              isSplitView
                ? mergedStyles.textAreaCellSubTitleStylesSplitView
                : textAreaStyles.cellSubTitle
            }
            updateInputRef={ref =>
              updateInputRef({ ref, refKey: refSubtitleKey })
            }
            onBlurInputField={() => {
              setFocusedCell(null);
              writeSinglePointRubricElementLocal({
                id: standardObj.id,
                params: { subTitle },
                type: "STANDARD",
                elementDetails: standardObj,
                isTitle: false,
              });
            }}
            onFocusInputField={() => {
              setFocusedCell(refSubtitleKey);
              setSelectedStandard({});
            }}
            minRows={1}
            containerStyle={{ marginTop: 8 }}
            emptyText={""}
            disabled={disabled}
            mode={mode}
            placeholder={t("common:click_to_add_withLabel", {
              label: t("common:definition"),
            })}
            name={"subTitle"}
            updateInputField={updateInputFieldSubtitle}
            className={classes.placeholderColor}
          />
        </React.Fragment>
      ) : null}
      {enableActionPopover ? (
        <Popover
          isOpen={true}
          align={"center"}
          positions={["left"]}
          containerStyle={mergedStyles.rowPopoverStyles}
          boundaryInset={20}
          reposition={true}
          content={() => {
            return (
              <div {...listeners} {...attributes}>
                <div
                  id={"SINGLE_POINT_RUBRIC_STANDARD"}
                  className={rowActionsClass}
                  ref={rowActionsRef}
                  onClick={e => {
                    e?.stopPropagation();
                    setSelectedStandard(
                      selectedStandard === standardObj.id
                        ? null
                        : standardObj.id
                    );
                  }}
                >
                  <DotsSixVerticalOutlined
                    size={"xx-small"}
                    style={styles.popoverIconStyle}
                  />
                </div>
              </div>
            );
          }}
          childrenRef={{ current: node.current }}
          isChildrenRefPassed={true}
        />
      ) : null}
      {enableActionPopoverOption ? (
        <Popover
          isOpen={true}
          align={"center"}
          boundaryInset={50}
          positions={["right"]}
          containerStyle={styles.popoverContainerStyle}
          reposition={true}
          onClickOutside={() => setSelectedStandard(null)}
          content={() => {
            return (
              <div
                className={classes.popoverRowActions}
                style={elevations["elevation-2B"]}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  style={styles.rotateIconStyle}
                  size={"small"}
                  variant="neutral"
                  type="plain"
                  onClick={onAddStandardAfter}
                  disabled={disabled || disableDragging}
                />

                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  size={"small"}
                  style={styles.transformIconStyle}
                  variant="neutral"
                  type="plain"
                  onClick={onAddStandardBefore}
                  disabled={disabled || disableDragging}
                />

                {showDeleteIcon ? (
                  <>
                    <div className={classes.popoverActionsSeparator} />

                    <IconButton
                      dsVersion="2.0"
                      icon={<DeleteOutlined variant="critical" />}
                      size={"small"}
                      variant="neutral"
                      type="plain"
                      onClick={toggleDeleteModal}
                      disabled={disabled || disableDragging}
                    />
                  </>
                ) : null}
              </div>
            );
          }}
          childrenRef={{
            current: rowActionsRef.current,
          }}
          isChildrenRefPassed={true}
        />
      ) : null}
      {showDeleteModal ? (
        <Dialog
          variant="delete-dialog"
          title={t("common:delete_with_label", {
            label: t("common:row"),
          })}
          message={t("common:delete_msg_with_label", {
            label: t("common:row"),
          })}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:delete")}
          onButtonOneClick={toggleDeleteModal}
          onButtonTwoClick={onConfirmDelete}
          isClosable={true}
          onClose={toggleDeleteModal}
        />
      ) : null}
    </div>
  );
};

const Rows = ({
  standardObj,
  getColumns,
  setHoverStandardDetails,
  selectedStandard,
  attributes,
  listeners,
  node,
  isDragging,
  rowIndex,
  disableDragging,
}) => {
  const rowClass = classNames({
    [classes.row]: true,
    [classes.selectedRow]: selectedStandard === standardObj.id,
  });

  return (
    <div
      className={rowClass}
      key={standardObj.id}
      onMouseEnter={() => {
        setHoverStandardDetails({
          id: standardObj.id,
        });
      }}
      onMouseLeave={() => {
        setHoverStandardDetails({});
      }}
    >
      {getColumns({
        standardObj,
        attributes,
        listeners,
        node,
        isDragging,
        rowIndex,
        disableDragging,
      })}
    </div>
  );
};

class SinglePointRubricTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inFocusRow: null,
      inFocusCell: null,
    };
    if (props.customRef) {
      props.customRef(this);
    }
    this.cellRefs = {};
    this.evaluationCellRefs = {};
    this.focusedCell = null;

    this.debouncedWriteSinglePointRubricElementLocal = _.debounce(
      props.writeSinglePointRubricElementLocal,
      1000
    );
  }

  blur = () => {
    if (this.focusedCell && this.cellRefs[this.focusedCell]) {
      this.cellRefs[this.focusedCell].blur();
    }
  };

  writeSinglePointRubricElement = ({
    id,
    type,
    params,
    elementDetails,
    isTitle,
  }) => {
    const { updateField, portalType } = this.props;
    this.debouncedWriteSinglePointRubricElementLocal({
      id,
      type,
      params,
      elementDetails,
    });
    updateField({
      key: `${id}_${isTitle ? "title" : "subTitle"}`,
      params: { id, type, params, portalType },
      type: "SINGLE_POINT_RUBRIC_ELEMENT",
    });
  };

  updateInputRef = ({ ref, refKey }) => {
    this.cellRefs[refKey] = ref;
  };

  setFocusedCell = refKey => {
    this.focusedCell = refKey;
  };

  getHeaderRow = () => {
    const {
      rubric,
      headerRowStyle = {},
      writeSinglePointRubricElementLocal,
    } = this.props;

    const { inFocusCell } = this.state;
    const criterias = _.get(rubric, "criterias", []);

    return (
      <div className={classes.pseudoRow}>
        <div className={classes.headerRow} style={headerRowStyle}>
          {_.map(criterias, criteriaObj => {
            return (
              <Header
                {...this.props}
                criteriaObj={criteriaObj}
                inFocusCell={inFocusCell}
                updateInputRef={this.updateInputRef}
                setFocusedCell={this.setFocusedCell}
                writeSinglePointRubricElement={
                  this.writeSinglePointRubricElement
                }
                writeSinglePointRubricElementLocal={
                  writeSinglePointRubricElementLocal
                }
                saveSinglePointRubricUpdateActivityStatus={
                  this.saveSinglePointRubricUpdateActivityStatus
                }
              />
            );
          })}
        </div>
      </div>
    );
  };

  getRows = () => {
    const {
      hoverStandardDetails,
      setHoverStandardDetails,
      selectedStandard,
    } = this.props;

    const standards = _.get(this.props.rubric, "standards", []);

    return (
      <SortableContext items={standards} strategy={verticalListSortingStrategy}>
        {_.map(standards, (standardObj, index) => {
          return (
            <DraggableRow
              key={standardObj?.id}
              id={standardObj?.id}
              {...this.props}
              standards={standards}
            >
              <Rows
                getColumns={this.getColumns}
                standardObj={standardObj}
                hoverStandardDetails={hoverStandardDetails}
                setHoverStandardDetails={setHoverStandardDetails}
                selectedStandard={selectedStandard}
                rowIndex={index}
              />
            </DraggableRow>
          );
        })}
      </SortableContext>
    );
  };

  getColumns = ({
    standardObj,
    attributes,
    listeners,
    node,
    isDragging,
    rowIndex,
    disableDragging,
  }) => {
    const { rubric, writeSinglePointRubricElementLocal } = this.props;
    const { inFocusCell } = this.state;

    const criterias = _.get(rubric, "criterias", []);

    return _.map(criterias, criteriaObj => {
      return (
        <Columns
          {...this.props}
          criteriaObj={criteriaObj}
          standardObj={standardObj}
          inFocusCell={inFocusCell}
          setFocusedCell={this.setFocusedCell}
          updateInputRef={this.updateInputRef}
          deleteSinglePointRubricStandard={this.deleteSinglePointRubricStandard}
          writeSinglePointRubricElement={this.writeSinglePointRubricElement}
          writeSinglePointRubricElementLocal={
            writeSinglePointRubricElementLocal
          }
          saveSinglePointRubricUpdateActivityStatus={
            this.saveSinglePointRubricUpdateActivityStatus
          }
          createSinglePointRubricStandard={this.createSinglePointRubricStandard}
          isDragging={isDragging}
          attributes={attributes}
          listeners={listeners}
          node={node}
          rowIndex={rowIndex}
          disableDragging={disableDragging}
        />
      );
    });
  };

  createSinglePointRubricStandard = async params => {
    const { putAfterItemId } = params;
    const {
      rubric: { id: rubricId },
      createSinglePointRubricStandard,
      setScrollToBottom,
      portalType,
    } = this.props;
    await createSinglePointRubricStandard({
      rubricId,
      portalType,
      putAfterItemId,
    });
    if (setScrollToBottom) {
      setTimeout(setScrollToBottom);
    }
  };

  deleteSinglePointRubricStandard = id => {
    const {
      rubric: { id: rubricId },
      deleteSinglePointRubricStandard,
      portalType,
    } = this.props;

    deleteSinglePointRubricStandard({ id, rubricId, portalType });
  };

  saveSinglePointRubricUpdateActivityStatus = () => {
    const { assignmentId, saveAssessmentToolUpdateStatusInRedux } = this.props;
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "SINGLE_POINT_RUBRIC",
    });
  };

  renderActionButtons = () => {
    const {
      mode,
      onClickToolSave,
      t,
      showSaveTemplateButton,
      bottomContainerStyle = {},
      showActionButtons = true,
      actionButtonsUIType = "BUTTON",
      rubric,
    } = this.props;

    if (mode === "view" || !showActionButtons) return null;
    const standards = _.get(rubric, "standards", []);

    const disabled = _.includes(_.last(standards)?.id, "NEW");

    const renderButtons = () => {
      switch (actionButtonsUIType) {
        case "BUTTON": {
          return (
            <>
              <Button
                dsVersion="2.0"
                size={"medium"}
                onClick={this.createSinglePointRubricStandard}
                variant="neutral"
                type="outlined"
                icon={
                  <AddColumnOutlined
                    size={"xx-small"}
                    style={styles.rotateIconStyle}
                  />
                }
                disabled={disabled}
              >
                {t("common:add_with_label", { label: t("common:row") })}
              </Button>
              {showSaveTemplateButton ? (
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size={"medium"}
                  onClick={onClickToolSave}
                >
                  {t("classRoom:save_as_template")}
                </Button>
              ) : null}
            </>
          );
        }
        case "ICON_BUTTON": {
          return (
            <>
              <Tooltip
                placement={"top"}
                tooltip={t("common:add_with_label", {
                  label: t("common:row"),
                })}
                showArrow={true}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  style={styles.rotateIconStyle}
                  size={"small"}
                  variant="neutral"
                  type="plain"
                  onClick={this.createSinglePointRubricStandard}
                  disabled={disabled}
                />
              </Tooltip>

              {showSaveTemplateButton ? (
                <Tooltip
                  placement={"top"}
                  tooltip={t("classRoom:save_as_template")}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AssessmentApplicableOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    onClick={onClickToolSave}
                  />
                </Tooltip>
              ) : null}
            </>
          );
        }
      }
    };

    return (
      <div className={classes.bottomContainer} style={bottomContainerStyle}>
        {renderButtons()}
      </div>
    );
  };

  render() {
    const { rubric } = this.props;

    return (
      <React.Fragment>
        <div
          onBlur={() => {
            this.setState({ inFocusRow: null, inFocusCell: null });
          }}
          className={classes.container}
        >
          {rubric ? (
            <React.Fragment>
              {this.getHeaderRow()}
              {this.getRows()}
            </React.Fragment>
          ) : (
            <div className={classes.emptyText} style={{ padding: "16px" }}>
              {` No Single Point Rubric defined`}
            </div>
          )}
        </div>
        {this.renderActionButtons()}
      </React.Fragment>
    );
  }
}

const SinglePointRubricTableWrapper = props => {
  const { rubric, reorderAssessmentToolItem } = props;
  const { standards } = rubric || {};
  const [hoverStandardDetails, setHoverStandardDetails] = useState({});
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [projectedLineData, setProjectedLineData] = useState({});
  const [indexToFocus, setIndexToFocus] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const onDragStart = event => {
    setIsDragging(true);
    setHoverStandardDetails({});
    setSelectedStandard(event.active.id);
  };

  const onDragMove = params => {
    const { active, over, delta } = params;
    const { id: overId, rect: overRect = {} } = over || {};
    const { id: activeId, rect: activeRect = {} } = active || {};
    const { current = {} } = activeRect || {};
    const { translated = {} } = current || {};
    if (_.isEmpty(translated) || !translated.left || !translated.right) {
      return;
    }
    const newProjectedLineData = getProjectionLineData({
      overId,
      overRect,
      activeId,
      delta,
      translated,
      dragType: "row",
      assessmentToolArray: standards,
    });

    if (!_.isEqual(projectedLineData, newProjectedLineData)) {
      setProjectedLineData(newProjectedLineData);
    }
    return;
  };

  const onDragEnd = async params => {
    setIsDragging(false);
    setSelectedStandard({});
    setProjectedLineData({});

    const { active, over } = params;
    const activeId = active.id;
    const overId = over.id;

    const fromIndex = _.findIndex(standards, { id: activeId });
    const toIndex = _.findIndex(standards, { id: overId });

    if (activeId !== overId) {
      let putAfterItemId = null;
      if (toIndex > 0) {
        if (fromIndex < toIndex) {
          putAfterItemId = _.get(standards, `${[toIndex]}.id`, null);
        } else {
          putAfterItemId = _.get(standards, `${[toIndex - 1]}.id`, null);
        }
      } else if (toIndex == _.size(standards) - 1) {
        putAfterItemId = _.get(standards.last(), `id`, null);
      }
      const itemType = "SINGLE_POINT_RUBRIC_STANDARD";
      const updatedValue = moveElementInArray({
        array: standards,
        fromIndex,
        toIndex,
      });
      await reorderAssessmentToolItem({
        itemId: activeId,
        itemType,
        putAfterItemId,
        toolType: SINGLE_POINT_RUBRIC,
        toolId: rubric.id,
        updatedValue,
      });
    }
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragMove={onDragMove}
    >
      <SinglePointRubricTable
        {...props}
        hoverStandardDetails={hoverStandardDetails}
        setHoverStandardDetails={setHoverStandardDetails}
        selectedStandard={selectedStandard}
        setSelectedStandard={setSelectedStandard}
        isDragging={isDragging}
        projectedLineData={projectedLineData}
        setIndexToFocus={setIndexToFocus}
        indexToFocus={indexToFocus}
      />
    </DndContext>
  );
};

const mapActionCreators = {
  updateField,
  writeSinglePointRubricElementLocal,
  createSinglePointRubricStandard,
  deleteSinglePointRubricStandard,
  updateEvaluationData,
  saveAssessmentToolUpdateStatusInRedux,
  reorderAssessmentToolItem,
};

const mapStateToProps = (state, ownProps) => {
  const { rubric } = ownProps;
  return {
    rubric: sortAssessmentTool({ tool: rubric, type: SINGLE_POINT_RUBRIC }),
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "common"] }),
  connect(mapStateToProps, mapActionCreators)
)(SinglePointRubricTableWrapper);

SinglePointRubricTableWrapper.defaultProps = {
  isEvaluationEnable: false,
  toolEvaluationResponses: [],
  containerStyle: {},
  showSaveTemplateButton: true,
};
